/** @file Settings › Keyboard shortcuts: every binding by context; rebind by pressing keys, unbind, reset. */
import type { Translate } from '@pose2d/i18n/types';
import { chordFromEvent } from '@pose2d/shortcuts/chordFromEvent';
import { chordLabel } from '@pose2d/shortcuts/chordLabel';
import type { Binding, KeyEventLike, Platform } from '@pose2d/shortcuts/types';
import { useState } from 'react';
import { errorText } from '../../shell/i18n/errorText';
import { rebindShortcut, type RebindProblem } from './rebindShortcut';
import { ShortcutRow } from './ShortcutRow';
import { shortcutRows, type ShortcutRowData } from './shortcutRows';
import './settings.css';

/** Props of ShortcutsSection. */
export interface ShortcutsSectionProps {
  t: Translate;
  /** Every default binding (allBindings()). */
  defaults: readonly Binding[];
  /** settings.ui.shortcuts as stored. */
  overrides: readonly Binding[];
  platform: Platform;
  /** Saves the whole override list; rejects on failure. */
  save: (overrides: Binding[]) => Promise<void>;
}

const rowKey = (row: Pick<Binding, 'id' | 'context'>): string => `${row.context}/${row.id}`;

function problemText(t: Translate, problem: RebindProblem, context: string, platform: Platform): string {
  const keys = chordLabel(problem.keys, platform);
  if (problem.code === 'RESERVED_KEY') return t('shortcutsPanel.reserved', { keys });
  if (problem.code === 'CONFLICT') return t('shortcutsPanel.conflict', { keys, other: problem.other ?? '', context });
  return t('shortcutsPanel.invalid', { keys });
}

/** A refused chord keeps the capture open with an inline notice; a saved one closes it. */
export function ShortcutsSection({ t, defaults, overrides, platform, save }: ShortcutsSectionProps) {
  const [capturing, setCapturing] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ row: string; text: string } | null>(null);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const store = (next: Binding[]): void => {
    setFailure(null);
    save(next).catch((thrown: unknown) => { setFailure({ thrown }); });
  };
  const apply = (row: ShortcutRowData, keys: string): void => {
    const result = rebindShortcut(defaults, overrides, row, keys, platform);
    if (!result.ok) {
      setNotice({ row: rowKey(row), text: problemText(t, result.problem, row.context, platform) });
      return;
    }
    setNotice(null);
    setCapturing(null);
    store(result.overrides);
  };
  const onKey = (row: ShortcutRowData, event: KeyEventLike): void => {
    const keys = chordFromEvent(event, platform);
    if (keys !== null) apply(row, keys);
  };
  return (
    <section className="shell-card settings-section" aria-labelledby="settings-shortcuts">
      <h2 id="settings-shortcuts">{t('settings.shortcuts')}</h2>
      <p className="shell-hint">{t('shortcutsPanel.hint')}</p>
      <table className="shortcut-table"><tbody>
        {shortcutRows(defaults, overrides, platform).map((row) => (
          <ShortcutRow key={rowKey(row)} t={t} row={row} platform={platform} capturing={capturing === rowKey(row)}
            notice={notice?.row === rowKey(row) ? notice.text : null} onKey={(event) => { onKey(row, event); }}
            onCapture={() => { setNotice(null); setCapturing(rowKey(row)); }} onCancel={() => { setCapturing(null); setNotice(null); }}
            onUnbind={() => { apply(row, ''); }} onReset={() => { apply(row, row.defaultKeys); }} />
        ))}
      </tbody></table>
      <button type="button" disabled={overrides.length === 0} onClick={() => { store([]); }}>{t('shortcutsPanel.resetAll')}</button>
      {failure === null ? null : <p role="alert" className="shell-error">{errorText(t, failure.thrown)}</p>}
    </section>
  );
}
